import { ChevronDownIcon } from '@heroicons/react/24/outline';
import Button from '../Button';
import { pascalCase } from 'change-case';
import { type FileAccessProviders, fileAccessProvidersList } from '~/@types/interface';
import { type Dispatch, type ReactNode, type SetStateAction } from 'react';
import { getProviderDisplayName } from '~/app/(secured)/[workspaceId]/communication-review/settings/_components/CommunicationPlatformModal';
import { getProviderText } from '~/utils/provider';
import { EcommsIntegrationProvider, type RemoteTaskManagerProvider } from '@prisma/client';
import { cn } from '~/lib/utils';

function getProviderName(
  provider: EcommsIntegrationProvider | FileAccessProviders | RemoteTaskManagerProvider,
): string {
  if (Object.values(EcommsIntegrationProvider).includes(provider as EcommsIntegrationProvider)) {
    return getProviderDisplayName(provider as EcommsIntegrationProvider);
  }
  if (fileAccessProvidersList.includes(provider as FileAccessProviders)) {
    return getProviderText(provider as FileAccessProviders);
  }
  return pascalCase(provider);
}

interface AllowAccessStepProps<T> {
  provider: EcommsIntegrationProvider | FileAccessProviders | RemoteTaskManagerProvider;
  permissions: Record<string, { title: string; points: string[] }[]>;
  setStep: Dispatch<SetStateAction<T>>;
  nextStep: T;
  /** Overrides auto-derived provider name (e.g. "SharePoint"). */
  providerLabel?: string;
  /** Optional mark above the title (File Access connect chrome). */
  icon?: ReactNode;
  className?: string;
}

export function AllowAccessStep<T>({
  provider,
  permissions,
  setStep,
  nextStep,
  providerLabel,
  icon,
  className,
}: AllowAccessStepProps<T>) {
  const scopes = permissions[provider] ?? [];
  const name = providerLabel ?? getProviderName(provider);
  const centered = Boolean(icon);

  return (
    <div className={cn('space-y-5', className)}>
      <div className={cn(centered && 'flex flex-col items-center text-center')}>
        {icon ? <div className="mb-4">{icon}</div> : null}
        <h3 className="text-foreground text-lg font-semibold tracking-tight">Allow Access</h3>
        <p
          className={cn(
            'text-muted-foreground mt-1 text-sm leading-5',
            centered && 'max-w-sm',
          )}
        >
          Protege is requesting access to your {name} account.
        </p>
      </div>

      {scopes.length > 0 ? (
        <div className={cn(centered && 'w-full text-left')}>
          <div className="mb-2.5">
            <h4 className="text-foreground text-[13px] font-medium tracking-tight">Read Data</h4>
            <p className="text-muted-foreground mt-0.5 text-xs leading-4">
              We can read the following types of data:
            </p>
          </div>
          <div className="border-border/80 overflow-hidden rounded-xl border shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
            {scopes.map(({ title, points }, index) => (
              <details
                key={`${title}-${index}`}
                className="border-border/80 group border-b last:border-b-0"
              >
                <summary className="hover:bg-accent/40 focus-visible:bg-accent/40 focus-visible:ring-ring flex cursor-pointer list-none items-center justify-between gap-2 px-3.5 py-2.5 text-[13px] font-medium tracking-tight outline-none focus-visible:ring-2 focus-visible:ring-inset [&::-webkit-details-marker]:hidden">
                  <span>{title}</span>
                  <ChevronDownIcon
                    className="text-muted-foreground size-3.5 shrink-0 transition-transform duration-150 group-open:rotate-180"
                    strokeWidth={2}
                  />
                </summary>
                <div className="bg-muted/40 text-muted-foreground border-border/80 border-t px-3.5 py-2.5 text-xs leading-5">
                  <ul className="space-y-1">
                    {points.map((point) => (
                      <li key={point} className="flex gap-2">
                        <span className="text-muted-foreground/50 shrink-0" aria-hidden>
                          ·
                        </span>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </details>
            ))}
          </div>
        </div>
      ) : null}

      <div className="space-y-3">
        <p className="text-muted-foreground text-xs leading-5">
          By proceeding, you acknowledge Protege may update the data fields it reads within this
          integration over time.
        </p>
        <Button
          label="Continue"
          size="sm"
          className="w-full"
          onClick={() => {
            setStep(nextStep);
          }}
        />
      </div>
    </div>
  );
}
